import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import DocumentStatsCards from "./components/DocumentStatsCards";
import DocumentTable from "./components/DocumentTable";
import ActivitiesPagination from "./components/ActivitiesPagination";
import CollapsibleSection from "./components/CollapsibleSection";
import { useGetDocumentsQuery, useGetDocumentStatsQuery, useDeleteDocumentMutation } from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";
import useSnackbar from "@/hooks/use-snackbar";

const DocumentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");

  const { data: documentsData, isLoading, refetch } = useGetDocumentsQuery({ page, limit, search });
  const { data: statsData, refetch: refetchStats } = useGetDocumentStatsQuery();
  const [deleteDocument] = useDeleteDocumentMutation();

  // Refetch data when navigating back from form page
  useEffect(() => {
    if (location.state?.refresh) {
      refetch();
      refetchStats();
      // Clear the state to prevent refetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetch, refetchStats, navigate, location.pathname]);

  const documents = documentsData?.data?.documents || [];
  const pagination = documentsData?.data?.pagination || {
    totalItems: 0,
    totalPages: 0,
    currentPage: page,
  };

  const stats = statsData?.data || {
    total: 0,
    activitiesDocs: 0,
    otherDocs: 0,
  };

  const canAddDocument = user?.role === Roles.SUPER_ADMIN || user?.role === Roles.ADMIN;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleUploadClick = () => {
    navigate("/documents/add");
  };

  const handleView = (document) => {
    if (document?.fileUrl) {
      window.open(document.fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleEdit = (document) => {
    navigate(`/documents/${document._id || document.id}/edit`);
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Are you sure you want to delete "${document.title}"?`)) {
      return;
    }

    try {
      const documentId = document._id || document.id;
      const { error } = await deleteDocument(documentId);
      if (error) {
        showSnackbar({
          message: error?.data?.message || "Failed to delete document",
          type: "error",
        });
        return;
      }
      showSnackbar({
        message: "Document deleted successfully",
        type: "success",
      });
      refetch();
    } catch (error) {
      showSnackbar({
        message: "An error occurred while deleting the document",
        type: "error",
      });
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Documents
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage and monitor all documents
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <CollapsibleSection
          title="Document Statistics"
          defaultExpanded={true}
          showCard={false}
        >
          <DocumentStatsCards stats={stats} onUploadClick={canAddDocument ? handleUploadClick : undefined} />
        </CollapsibleSection>

        {/* Documents Table */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <DocumentTable
            documents={documents}
            isLoading={isLoading}
            onView={handleView}
            onEdit={canAddDocument ? handleEdit : undefined}
            onDelete={canAddDocument ? handleDelete : undefined}
          />

          {/* Pagination */}
          <ActivitiesPagination
            page={pagination.currentPage}
            limit={limit}
            total={pagination.totalItems}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default DocumentsPage;



